import { Box, Flex, IconButton, Tooltip, useBoolean } from '@chakra-ui/react';
import { ReactComponent as LockIcon } from 'assets/icons/locked.svg';
import { LOCK_COLOR } from 'constants/bounding-box';
import { store } from 'modules/Editor/store';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { memo, useEffect } from 'react';

import { useEditor } from './useEditor';
import { useWidget } from './useWidget';

const LOCK_BUTTON_WIDTH = 24;
const LOCK_CONTAINER_PADDING = 1;
const MIN_DIM = LOCK_BUTTON_WIDTH + 2 * LOCK_CONTAINER_PADDING;

const HOVER_BORDER_WIDTH = 1;

/**
 * LOCKED WIDGET OVERLAY
 *
 * Handles locked widget hover + select UI states
 * Renders an `Unlock` button that user can click to unlock the widget
 */
export const LockedWidgetOverlay = memo(() => {
  const [isHovered, setIsHovered] = useBoolean(false);
  const { isLocked, widthPx, heightPx, rotateDeg, updateWidget, isWidgetSelected } = useWidget();
  const { zoom } = useEditor();

  const unlockWidget = () => {
    const activeWidgets = selectActiveWidgets(store.getState());
    const groupMembers = activeWidgets[0]?.groupMembers;
    const groupId = activeWidgets[0]?.groupId;

    // unlock group members if widget is part of a group
    const updatedGroupWidgets = groupMembers?.map((widgetId) => {
      return { widgetId, widgetData: { isLocked: false } };
    });
    // update group members with the new locked state if widget is part of a group, otherwise just unlock the widget
    updateWidget({ isLocked: false }, [
      ...updatedGroupWidgets,
      { widgetId: groupId, widgetData: { isLocked: !isLocked } },
    ]);
  };

  // Reset hover state when widget is unlocked
  useEffect(() => {
    if (!isLocked) {
      setIsHovered.off();
    }
  }, [isLocked, setIsHovered]);

  if (!isLocked) {
    return null;
  }

  return (
    <Flex
      position='absolute'
      w='full'
      h='full'
      top={0}
      boxShadow={isHovered ? `0 0 0 ${HOVER_BORDER_WIDTH}px ${LOCK_COLOR}` : ''}
      justifyContent={widthPx < MIN_DIM ? 'center' : 'flex-end'}
      alignItems={heightPx < MIN_DIM ? 'center' : 'flex-start'}
      onMouseEnter={setIsHovered.on}
      onMouseLeave={setIsHovered.off}
      // Ignore pointer events when widget is selected - some widgets have custom behaviour for click events
      //(i.e. text + table widgets)
      pointerEvents={isWidgetSelected ? 'none' : 'auto'}
      p={LOCK_CONTAINER_PADDING}
    >
      {(isWidgetSelected || isHovered) && (
        <Tooltip hasArrow bg='black' label={'Unlock'}>
          {/* Container to scale button based on zoom so that it keeps the same size for any zoom level */}
          <Box transform={`scale(${1 / zoom})`} transformOrigin={'top right'}>
            <IconButton
              icon={<LockIcon width={16} height={16} color='var(--vg-colors-red-500)' />}
              aria-label={'Unlock Widget'}
              onClick={unlockWidget}
              borderRadius='50%'
              bg={'white'}
              size={'xs'}
              boxShadow='0px 0px 4px 0px #0000007A'
              data-testid={'lock-widget-overlay-btn'}
              _hover={{ bg: 'gray.100' }}
              transform={`rotate(${-rotateDeg}deg) `}
              pointerEvents={'auto'}
            />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
});
LockedWidgetOverlay.displayName = 'LockedWidgetOverlay';
